/**
 * The browser-side draft of a line's equipment (LT-3597).
 *
 * One draft per line, in localStorage, keyed by TENANT, USER, facility and cell:
 * `lineDraft:<tenant>:<user>:<facilityId>:<cellId>`. Facility ids repeat across tenants, and
 * on a shared plant-floor browser one admin must never see another's draft. The identity
 * comes from the id token (`sub`, `custom:TenantID`), the way the rest of the app reads it.
 *
 * The draft holds DECISIONS, not rows: which types, their alias and description, which
 * sensors are not fitted, and — once Save has created the rows — the ids and numbers the
 * server assigned, so a resumed Save never creates them twice. Nothing in here is a sensor
 * row; those come from the equipment type's template at render time.
 *
 * Every storage access is wrapped: localStorage throws in some privacy modes and on quota,
 * and the page must still work without it (it says so, and keeps the draft in memory).
 *
 * Drafts are cleared at logout (Sean, 2026-10-09). `authReducer.logout` removes every key
 * under LINE_DRAFT_KEY_PREFIX — exported from this module, which deliberately imports
 * nothing heavy, so the auth module graph stays small (the rampLossPresets precedent).
 */
import { decodeToken } from "./tokenUtils";

export const LINE_DRAFT_KEY_PREFIX = "lineDraft:";
export const LINE_DRAFT_VERSION = 1;

export interface DraftIdentity {
    tenantId: string;
    userId: string;
}

/** `${equipmentTypeComponentsId}:${equipmentTypeProcessComponentsId}` — names a sensor in a TYPE's template. */
export type DraftSensorKey = string;

export const sensorKey = (equipmentTypeComponentsId: number, equipmentTypeProcessComponentsId: number): DraftSensorKey =>
    `${equipmentTypeComponentsId}:${equipmentTypeProcessComponentsId}`;

export interface LineDraftItem {
    /** Stable within the draft; never an equipment id. */
    draftId: string;
    equipmentTypeId: number;
    /** The type's label: the instance name is this plus the number (Extruder2). */
    equipmentTypeLabel: string;
    alias: string;
    /**
     * The alias was accepted as the default (Enter, or left blank), not typed. Save then
     * sends NO alias and the server sets it to the instance name with the REAL number — the
     * page's copy is built from an estimate.
     */
    aliasFromDefault?: boolean;
    notFitted: DraftSensorKey[];
    /** Distance from datum in metres (LT-3622). Optional: blank saves as 0, as before. */
    distance?: number;
    /**
     * Set the moment the batch create returns — before any sensor is created. `name` is the
     * instance name the SERVER settled on, which can differ from the page's estimate.
     */
    created?: { id: number; number: number; name?: string };
    /** The sensor tree exists and the not-fitted choices were applied. */
    sensorsDone?: boolean;
    lastError?: { status?: number; detail: string };
}

export interface LineDraft {
    version: typeof LINE_DRAFT_VERSION;
    tenantId: string;
    userId: string;
    facilityId: number;
    cellId: number;
    facilityName: string;
    cellName: string;
    updatedAt: string;
    items: LineDraftItem[];
    /** Present from the first Save click until every item is done. */
    save?: { startedAt: string };
}

export const draftKey = (identity: DraftIdentity, facilityId: number, cellId: number): string =>
    `${LINE_DRAFT_KEY_PREFIX}${identity.tenantId}:${identity.userId}:${facilityId}:${cellId}`;

const identityPrefix = (identity: DraftIdentity) => `${LINE_DRAFT_KEY_PREFIX}${identity.tenantId}:${identity.userId}:`;

/** The user and tenant a draft belongs to, from the id token; null when it cannot be read. */
export const draftIdentityFromIdToken = (idToken?: string | null): DraftIdentity | null => {
    if (!idToken) return null;
    try {
        const decoded = decodeToken(idToken);
        const userId = decoded?.sub;
        const tenantId = decoded?.["custom:TenantID"];
        if (!userId || !tenantId) return null;
        return { tenantId: String(tenantId), userId: String(userId) };
    } catch {
        return null;
    }
};

/** True when localStorage can be written right now. Probed, not assumed. */
export const storageAvailable = (): boolean => {
    try {
        const probe = `${LINE_DRAFT_KEY_PREFIX}__probe__`;
        window.localStorage.setItem(probe, "1");
        window.localStorage.removeItem(probe);
        return true;
    } catch {
        return false;
    }
};

const allKeys = (): string[] => {
    try {
        const keys: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (key && key.startsWith(LINE_DRAFT_KEY_PREFIX)) keys.push(key);
        }
        return keys;
    } catch {
        return [];
    }
};

const parse = (raw: string | null): LineDraft | null => {
    if (!raw) return null;
    try {
        const draft = JSON.parse(raw);
        if (!draft || draft.version !== LINE_DRAFT_VERSION || !Array.isArray(draft.items)) return null;
        return draft as LineDraft;
    } catch {
        return null;
    }
};

export const loadDraft = (key: string): LineDraft | null => {
    try {
        return parse(window.localStorage.getItem(key));
    } catch {
        return null;
    }
};

/** Writes the draft under its own key. Returns false when storage refused it. */
export const saveDraft = (draft: LineDraft): boolean => {
    try {
        const key = draftKey({ tenantId: draft.tenantId, userId: draft.userId }, draft.facilityId, draft.cellId);
        window.localStorage.setItem(key, JSON.stringify(draft));
        return true;
    } catch {
        return false;
    }
};

export const removeDraft = (key: string): void => {
    try {
        window.localStorage.removeItem(key);
    } catch {
        /* nothing to remove, or storage unavailable */
    }
};

/** Every draft of THIS user on this browser, newest first. */
export const listDrafts = (identity: DraftIdentity): LineDraft[] => {
    const prefix = identityPrefix(identity);
    const drafts: LineDraft[] = [];
    for (const key of allKeys()) {
        if (!key.startsWith(prefix)) continue;
        const draft = loadDraft(key);
        if (draft) drafts.push(draft);
    }
    return drafts.sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
};

/**
 * Remove drafts left by OTHER users — a browser closed without logging out leaves them
 * behind. Called when the page opens, so the next person never meets them. Returns the count.
 */
export const purgeDraftsNotOwnedBy = (identity: DraftIdentity): number => {
    const prefix = identityPrefix(identity);
    let removed = 0;
    for (const key of allKeys()) {
        if (key.startsWith(prefix)) continue;
        removeDraft(key);
        removed++;
    }
    return removed;
};

/**
 * Logout: every draft, whoever's — except, on a FORCED logout (session expiry, a failed token
 * refresh), drafts with an incomplete Save. Those hold the ids of equipment rows that exist
 * but have no sensors yet; nothing else points at them, and the forced path cannot warn
 * (Sean, 2026-10-09). The explicit header logout, which warns first, clears them too.
 * Returns the number removed.
 */
export const clearLineDrafts = (options: { keepIncomplete?: boolean } = {}): number => {
    let removed = 0;
    for (const key of allKeys()) {
        if (options.keepIncomplete && draftIsIncomplete(loadDraft(key))) continue;
        removeDraft(key);
        removed++;
    }
    return removed;
};

/** The explicit logout: everything goes. */
export const clearAllLineDrafts = (): number => clearLineDrafts();

export const newDraft = (
    identity: DraftIdentity,
    line: { facilityId: number; cellId: number; facilityName: string; cellName: string },
): LineDraft => ({
    version: LINE_DRAFT_VERSION,
    tenantId: identity.tenantId,
    userId: identity.userId,
    facilityId: line.facilityId,
    cellId: line.cellId,
    facilityName: line.facilityName,
    cellName: line.cellName,
    updatedAt: new Date().toISOString(),
    items: [],
});

let draftIdCounter = 0;
export const newDraftId = (): string => `d${Date.now().toString(36)}${(draftIdCounter++).toString(36)}`;

/** A Save was started and at least one item still has no sensors (or no row). */
export const draftIsIncomplete = (draft: LineDraft | null | undefined): boolean =>
    !!draft?.save && draft.items.some((item) => !item.sensorsDone);

/** Items that already have rows but no sensors yet — what Resume has to finish. */
export const itemsAwaitingSensors = (draft: LineDraft): LineDraftItem[] =>
    draft.items.filter((item) => item.created && !item.sensorsDone);

/**
 * The instance name (master.equipment.description) is NOT typed in: it is the type label plus
 * the equipment's count on the line — Extruder2 for the second ExtruderTP (Sean, 2026-10-09).
 * The server derives it when it assigns the number; this is the page's estimate of the same.
 */
export const instanceName = (item: LineDraftItem, number?: number): string =>
    `${item.equipmentTypeLabel}${number ?? ""}`;

/**
 * How instance names are compared for a clash — case- and whitespace-insensitively, the same
 * fold db_api's batch create uses (LT-3622).
 */
export const nameKey = (name: string | null | undefined): string => (name ?? "").trim().toLowerCase();

/** True when Save should let the server set the alias (the default), not send the page's copy. */
export const aliasIsDefault = (item: LineDraftItem): boolean => !!item.aliasFromDefault || !item.alias.trim();

/**
 * The alias the equipment will carry: what was typed, else its instance name `name` (the
 * collision-checked one, LT-3622). An ACCEPTED default follows the instance name live —
 * removing an earlier item moves this one's name, and the alias must move with it, not keep
 * the text it was accepted as.
 */
export const effectiveAlias = (item: LineDraftItem, name: string): string =>
    aliasIsDefault(item) ? name : item.alias.trim();

export type DraftProblem =
    | { kind: "typeUnavailable"; draftId: string; equipmentTypeLabel: string }
    | { kind: "empty" };

/** What blocks Save. `availableTypeIds` is the live, active, non-component-only type list. */
export const draftProblems = (draft: LineDraft, availableTypeIds: Set<number>): DraftProblem[] => {
    const problems: DraftProblem[] = [];
    const pending = draft.items.filter((item) => !item.created);
    if (draft.items.length === 0) problems.push({ kind: "empty" });
    for (const item of pending) {
        // A blank alias is not a problem: the default is the instance name.
        if (!availableTypeIds.has(item.equipmentTypeId)) {
            problems.push({ kind: "typeUnavailable", draftId: item.draftId, equipmentTypeLabel: item.equipmentTypeLabel });
        }
    }
    return problems;
};
